import { NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { debounceTime, filter, interval, merge, Subscription, timer } from 'rxjs';

import { ACTIVE_SESSIONS_ROUTE } from '@app/active-sessions/tours/active-sessions.constants';
import { CredentialsService } from '@app/auth/credentials.service';
import { PasskeyService } from '@app/auth/passkey.service';
import { GuidedTourService } from '../../guided-tour/guided-tour.service';

/** Espera tras montar el layout: no compite con la carga inicial de la página. */
const START_DELAY_MS = 4000;

/** Tras navegar se deja que la pantalla nueva termine de montarse. */
const NAVIGATION_DEBOUNCE_MS = 1500;

/**
 * El refresh del token no avisa a nadie (`CredentialsService` no expone eventos), así
 * que además de navegar se revisa el claim cada tanto. Decodificar es barato.
 */
const CLAIM_POLL_MS = 60 * 1000;

/** Pedir la lista de llaves es lo caro: como mucho una vez en este lapso. */
const FIND_MINE_THROTTLE_MS = 5 * 60 * 1000;

/** Marca por usuario; se guarda con el prefijo y la versión del GuidedTourService. */
const INTRO_ID = 'passkeys.intro';

/**
 * Fase 7 — aviso discreto que presenta las llaves de acceso cuando la política se
 * habilita para el usuario (claim `passkeysAllowed`). Sin velo ni pasos: una tarjeta
 * bajo el menú de perfil con «Configurar» y «Ahora no».
 *
 * Sale una sola vez por usuario y navegador, y sólo si todavía no tiene llaves. El
 * tour completo sigue viviendo en Sesiones activas; «Configurar» lleva ahí.
 */
@Component({
    selector: 'app-passkeys-intro',
    standalone: true,
    imports: [NgIf, ButtonModule],
    templateUrl: './passkeys-intro.component.html',
    styleUrls: ['./passkeys-intro.component.scss'],
})
export class PasskeysIntroComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly credentialsService = inject(CredentialsService);
    private readonly passkeyService = inject(PasskeyService);
    private readonly tourService = inject(GuidedTourService);

    visible = false;

    private subscription?: Subscription;
    private checking = false;
    /** Último claim visto: si pasa de apagado a encendido se ignora el throttle. */
    private lastAllowed = false;
    private lastFindAt = 0;

    ngOnInit(): void {
        this.subscription = merge(
            timer(START_DELAY_MS),
            this.router.events.pipe(
                filter((event) => event instanceof NavigationEnd),
                debounceTime(NAVIGATION_DEBOUNCE_MS)
            ),
            interval(CLAIM_POLL_MS)
        ).subscribe(() => void this.check());
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    configure(): void {
        this.dismiss();
        // El fragmento lo usa la tarjeta para hacer scroll hasta ella al cargar.
        void this.router.navigate([ACTIVE_SESSIONS_ROUTE], { fragment: 'passkeys' });
    }

    dismiss(): void {
        this.visible = false;
        const userId = this.userId();
        if (userId !== undefined) {
            this.tourService.markSeen(this.introId(userId));
        }
    }

    private async check(): Promise<void> {
        const allowed = this.passkeyService.isAllowedForCurrentUser();
        const justEnabled = allowed && !this.lastAllowed;
        this.lastAllowed = allowed;

        if (this.visible) {
            // Se apagó la política: el aviso se va sin marcarse.
            if (!allowed) {
                this.visible = false;
            } else if (this.atActiveSessions()) {
                // Llegó sola a la tarjeta: ahí ya la presenta el tour.
                this.dismiss();
            }
            return;
        }

        if (this.checking || !this.canShow()) {
            return;
        }
        if (!justEnabled && Date.now() - this.lastFindAt < FIND_MINE_THROTTLE_MS) {
            return;
        }

        this.checking = true;
        this.lastFindAt = Date.now();
        try {
            const passkeys = await this.passkeyService.list();
            if (passkeys.length > 0) {
                // Ya las conoce: no hace falta presentarlas nunca más.
                this.dismiss();
                return;
            }
            // Mientras llegaba la respuesta pudo navegar o abrir un diálogo.
            this.visible = this.canShow();
        } catch {
            // Si no se pudo saber, no se muestra.
        } finally {
            this.checking = false;
        }
    }

    private canShow(): boolean {
        const userId = this.userId();
        return (
            userId !== undefined &&
            this.passkeyService.isAllowedForCurrentUser() &&
            this.passkeyService.isSupported() &&
            !this.tourService.isSeen(this.introId(userId)) &&
            !this.tourService.isActive &&
            // El DTO generado lo tipa como string; el backend manda boolean.
            String(this.credentialsService.credentials?.isDefaultPassword) !== 'true' &&
            !this.atActiveSessions() &&
            !this.isBusy()
        );
    }

    /** Un diálogo modal o un banner global abiertos: se espera a la siguiente revisión. */
    private isBusy(): boolean {
        return !!document.querySelector(
            '.p-dialog-mask.p-component-overlay, .layout-banners .maintenance-banner'
        );
    }

    private atActiveSessions(): boolean {
        return this.router.url.split(/[?#]/)[0].startsWith(ACTIVE_SESSIONS_ROUTE);
    }

    private userId(): number | undefined {
        return this.credentialsService.getDecodedToken()?.id ?? undefined;
    }

    private introId(userId: number): string {
        return `${INTRO_ID}.${userId}`;
    }
}
